import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { NeedsHousehold, SectionTitle, useConfirm, useNotify, useRun, useUndoable } from '../common';
import { TAGS, frequencyText, newGuid, number, parseNumber, tagText, toggleIn } from '../labels';
import { IngredientLine, ingredientDetail, prepStepText, toIngredientRequest } from './recipeText';
import { IngredientDialog } from './IngredientDialog';
import { AliasEditor } from '../editing';

const toLine = (i: T.RecipeIngredient): IngredientLine => ({
  key: newGuid(), catalogItemPublicId: i.catalogItemPublicId, name: i.name, quantity: i.quantity, unit: i.unit, note: i.note,
});

/** A catalog entry for an ingredient whose item is not among the food items (so the picker still shows it). */
const itemOf = (i: T.RecipeIngredient): T.CatalogItem => ({
  publicId: i.catalogItemPublicId, name: i.name, kind: 'Food', defaultUnit: i.unit ?? 'Unit', storeSection: i.storeSection,
  isPantryStaple: i.isPantryStaple, recipeCount: 0, aliases: [], preparedItems: [],
});

/** Create (/casa/receitas/nova) or edit (/casa/receitas/:id) a recipe with its ingredients and prep steps. */
function RecipeEditContent() {
  const { id } = useParams();
  const loadedId = id ?? null;
  // "Duplicar receita" turns the page into a new recipe with what is on screen; saving creates it
  const [copying, setCopying] = useState(false);
  const recipeId = copying ? null : loadedId;
  const navigate = useNavigate();
  const notify = useNotify();
  const run = useRun();
  const offerUndo = useUndoable();
  const [confirm, confirmDialog] = useConfirm();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [sideRecipes, setSideRecipes] = useState<T.RecipeSide[]>([]);
  const [items, setItems] = useState<T.CatalogItem[]>([]);

  const [name, setName] = useState('');
  const [course, setCourse] = useState<T.RecipeCourse>('Main');
  const [usualSides, setUsualSides] = useState<T.RecipeSide[]>([]);
  const [servingsText, setServingsText] = useState('4');
  const [prepText, setPrepText] = useState('');
  const [frequency, setFrequency] = useState<T.RecipeFrequency>('Habitual');
  const [markedQuick, setMarkedQuick] = useState(false);
  const [leftovers, setLeftovers] = useState(false);
  const [potDish, setPotDish] = useState(false);
  const [tags, setTags] = useState<T.RecipeTag[]>([]);
  const [notes, setNotes] = useState('');
  const [link, setLink] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [ingredients, setIngredients] = useState<IngredientLine[]>([]);
  const [prepSteps, setPrepSteps] = useState<T.RecipePrepStep[]>([]);

  // Dialogs: the ingredient being edited (null = new), the prep step (index, -1 = new), the sides chooser
  const [ingredientDialog, setIngredientDialog] = useState<{ line: IngredientLine | null } | null>(null);
  const [stepDialog, setStepDialog] = useState<number | null>(null);
  const [sidesOpen, setSidesOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setLoadError(null);
      savedState.current = null;
      setCopying(false);
      try {
        const [recipes, food, recipe] = await Promise.all([
          api.getRecipes(),
          api.getCatalogItems('Food'),
          loadedId ? api.getRecipe(loadedId) : Promise.resolve(null),
        ]);
        if (!alive) return;
        setSideRecipes(recipes.filter(r => r.course === 'Side' && r.publicId !== loadedId).map(r => ({ publicId: r.publicId, name: r.name })));
        const missing = recipe ? recipe.ingredients.filter(i => !food.some(f => f.publicId === i.catalogItemPublicId)).map(itemOf) : [];
        setItems(food.concat(missing));
        if (recipe) {
          setName(recipe.name);
          setCourse(recipe.course);
          setUsualSides(recipe.usualSides);
          setServingsText(number(recipe.baseServings, 2));
          setPrepText(recipe.prepMinutes !== null && recipe.prepMinutes !== undefined ? String(recipe.prepMinutes) : '');
          setFrequency(recipe.frequency);
          setMarkedQuick(recipe.isMarkedQuick);
          setLeftovers(recipe.yieldsLeftovers);
          setPotDish(recipe.isPotDish);
          setTags(recipe.tags);
          setNotes(recipe.notes ?? '');
          setLink(recipe.link ?? '');
          setAliases(recipe.aliases);
          setIngredients(recipe.ingredients.map(toLine));
          setPrepSteps(recipe.prepSteps);
        }
      } catch (e: any) {
        if (alive) setLoadError(e.message ?? 'Ocorreu um erro.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadedId]);

  const back = () => navigate('/casa/receitas');

  // What the page holds now, to tell whether leaving would lose changes (the line keys are new on each load)
  const state = useMemo(() => JSON.stringify([
    name, course, usualSides.map(s => s.publicId), servingsText, prepText, frequency, markedQuick, leftovers, potDish,
    tags, notes, link, aliases, ingredients.map(toIngredientRequest), prepSteps,
  ]), [name, course, usualSides, servingsText, prepText, frequency, markedQuick, leftovers, potDish, tags, notes, link,
    aliases, ingredients, prepSteps]);
  const savedState = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && !loadError && savedState.current === null) savedState.current = state;
  }, [loading, loadError, state]);
  const dirty = savedState.current !== null && state !== savedState.current;

  // Closing or reloading the tab with changes: the browser asks (its own text)
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** The arrow and "Cancelar": with changes not saved, ask before they are lost. */
  const leave = async () => {
    if (dirty && !await confirm({ title: 'Sair sem guardar?', text: copying ? 'A cópia não fica guardada.' : 'As alterações a esta receita perdem-se.', confirmText: 'Sair', danger: true })) return;
    back();
  };

  const detailsSummary = useMemo(() => {
    const parts = [frequencyText(frequency)];
    if (markedQuick) parts.push('rápida');
    if (leftovers) parts.push('dá sobras');
    if (potDish) parts.push('em panela');
    TAGS.filter(t => tags.includes(t.value)).forEach(t => parts.push(t.text));
    return parts.join(' · ');
  }, [frequency, markedQuick, leftovers, potDish, tags]);
  const prepSummary = prepSteps.length === 0 ? 'Nenhuma' : prepSteps.length === 1 ? prepStepText(prepSteps[0]) : `${prepSteps.length} passos`;
  const notesSummary = [notes.trim(), link.trim() ? 'com link' : ''].filter(Boolean).join(' · ') || 'Nenhuma';
  const aliasesSummary = aliases.length === 0 ? 'Nenhum' : aliases.join(', ');

  const toggleTag = (tag: T.RecipeTag) =>
    setTags(current => toggleIn(current, tag));

  const saveIngredient = (line: IngredientLine) => {
    if (ingredientDialog?.line) {
      const key = ingredientDialog.line.key;
      setIngredients(current => current.map(l => (l.key === key ? { ...line, key } : l)));
    } else {
      setIngredients(current => current.concat({ ...line, key: newGuid() }));
    }
    setIngredientDialog(null);
  };

  const removeIngredient = () => {
    const key = ingredientDialog?.line?.key;
    setIngredients(current => current.filter(l => l.key !== key));
    setIngredientDialog(null);
  };

  const moveIngredient = (index: number, delta: number) => setIngredients(current => {
    const next = current.slice();
    const [line] = next.splice(index, 1);
    next.splice(index + delta, 0, line);
    return next;
  });

  const save = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) { setError('Indica o nome da receita.'); return; }
    const servings = parseNumber(servingsText);
    if (servings === null || servings <= 0) { setError('Indica para quantas doses é a receita.'); return; }
    let prepMinutes: number | null = null;
    if (prepText.trim() !== '') {
      const minutes = Number(prepText.trim());
      if (!Number.isInteger(minutes) || minutes <= 0) { setError('O tempo deve ser um número de minutos.'); return; }
      prepMinutes = minutes;
    }
    const request: T.SaveRecipeRequest = {
      name: trimmed,
      frequency,
      baseServings: servings,
      prepMinutes,
      isMarkedQuick: markedQuick,
      yieldsLeftovers: leftovers,
      isPotDish: potDish,
      tags: TAGS.map(t => t.value).filter(t => tags.includes(t)),
      notes: notes.trim() || null,
      link: link.trim() || null,
      ingredients: ingredients.map(toIngredientRequest),
      prepSteps,
      course,
      usualSidePublicIds: course !== 'Main' ? [] : usualSides.map(s => s.publicId),
      aliases,
    };
    setBusy(true);
    try {
      if (recipeId) await api.updateRecipe(recipeId, request);
      else await api.createRecipe(request);
      notify(recipeId ? 'Receita guardada.' : 'Receita criada.', { severity: 'success' });
      back();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  /** "Bifes de frango" → "Bifes de peru": change the ingredient and the name, then save. The other names stay with the original. */
  const duplicate = () => {
    setCopying(true);
    setName(current => `${current.trim()} (cópia)`);
    setAliases([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    notify('Cópia por guardar: troca o que muda e guarda.');
  };

  const remove = async () => {
    if (!recipeId) return;
    if (!await confirm({ title: 'Apagar', text: `Apagar a receita "${name}"?`, confirmText: 'Apagar', danger: true })) return;
    setBusy(true);
    const ok = await run(() => api.deleteRecipe(recipeId));
    setBusy(false);
    if (ok === undefined) return;
    back();
    // The recipe is only hidden, so "Desfazer" brings it back and opens it again (the list behind is out of date)
    offerUndo(`${name}: apagada`, () => api.restoreRecipe(recipeId), () => navigate(`/casa/receitas/${recipeId}`),
      { success: `${name}: recuperada` });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (loadError) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={back}>Voltar</Button>}>{loadError}</Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <IconButton aria-label="Voltar" onClick={leave}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>{copying ? 'Duplicar receita' : recipeId ? 'Editar receita' : 'Nova receita'}</Typography>
      </Stack>

      <Stack spacing={2}>
        <TextField label="Nome" placeholder="Ex.: Bacalhau à Brás" value={name} onChange={e => setName(e.target.value)} autoFocus={!recipeId} />

        <TextField select label="Tipo" value={course} onChange={e => setCourse(e.target.value as T.RecipeCourse)}>
          <MenuItem value="Main">Prato</MenuItem>
          <MenuItem value="Side">Acompanhamento (arroz, batata, salada…)</MenuItem>
          <MenuItem value="Starter">Entrada (sopa, petiscos…)</MenuItem>
          <MenuItem value="Dessert">Sobremesa</MenuItem>
        </TextField>

        {course === 'Main' && (
          <Box>
            <Typography variant="subtitle2">Acompanhamentos habituais</Typography>
            <Typography variant="caption" color="text.secondary">
              Os que costumas fazer com este prato. Ao planear escolhes um (ou vários) e a sugestão vai alternando.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {usualSides.map(s => (
                <Chip key={s.publicId} label={s.name} color="primary" onDelete={() => setUsualSides(current => current.filter(c => c.publicId !== s.publicId))} />
              ))}
              <Chip label="+ Juntar" color="primary" variant="outlined" onClick={() => setSidesOpen(true)} />
            </Box>
          </Box>
        )}

        <Stack direction="row" spacing={2}>
          <TextField label="Doses" value={servingsText} onChange={e => setServingsText(e.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} sx={{ flex: 1 }} />
          <TextField label="Tempo (min)" placeholder="Opcional" value={prepText} onChange={e => setPrepText(e.target.value)} slotProps={{ htmlInput: { inputMode: 'numeric' } }} sx={{ flex: 1 }} />
        </Stack>
      </Stack>

      <SectionTitle>Ingredientes</SectionTitle>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        A quantidade é opcional. Escreve-a para as doses indicadas acima.
      </Typography>
      {ingredients.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 1 }}>
          <List disablePadding dense>
            {ingredients.map((line, index) => {
              const detail = ingredientDetail(line);
              return (
                <ListItem
                  key={line.key}
                  divider={index < ingredients.length - 1}
                  disablePadding
                  secondaryAction={
                    <Stack direction="row">
                      <IconButton size="small" aria-label="Subir" disabled={index === 0} onClick={() => moveIngredient(index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                      <IconButton size="small" aria-label="Descer" disabled={index === ingredients.length - 1} onClick={() => moveIngredient(index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                    </Stack>
                  }
                >
                  <ListItemButton onClick={() => setIngredientDialog({ line })} sx={{ pr: 11 }}>
                    <ListItemText
                      primary={line.name}
                      secondary={[detail, line.catalogItemPublicId ? '' : 'artigo novo'].filter(Boolean).join(' · ') || undefined}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setIngredientDialog({ line: null })} sx={{ mb: 2 }}>Ingrediente</Button>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Rápida, sobras e etiquetas</Typography>
            <Typography variant="caption" color="text.secondary">{detailsSummary}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <TextField select label="Frequência" value={frequency} onChange={e => setFrequency(e.target.value as T.RecipeFrequency)}>
              <MenuItem value="Habitual">Habitual</MenuItem>
              <MenuItem value="Occasional">Ocasional</MenuItem>
            </TextField>
            <SwitchRow label="Rápida" hint="Já conta como rápida se demorar até 30 min." checked={markedQuick} onChange={setMarkedQuick} />
            <SwitchRow label="Dá para sobras" hint="Aguenta bem para o almoço do dia seguinte." checked={leftovers} onChange={setLeftovers} />
            <SwitchRow label="Faz-se em panela" hint="Sopas e caldos: dá para vários dias e marca-se na linha Sopa da semana." checked={potDish} onChange={setPotDish} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Etiquetas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TAGS.map(t => (
                  <Chip
                    key={t.value}
                    label={tagText(t.value)}
                    color="primary"
                    variant={tags.includes(t.value) ? 'filled' : 'outlined'}
                    onClick={() => toggleTag(t.value)}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Preparação antecipada</Typography>
            <Typography variant="caption" color="text.secondary">{prepSummary}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary">Ex.: demolhar o bacalhau 1 dia antes. Vai gerar uma tarefa.</Typography>
          {prepSteps.length > 0 && (
            <List dense disablePadding>
              {prepSteps.map((step, index) => (
                <ListItem
                  key={`${index}-${step.title}`}
                  disablePadding
                  secondaryAction={
                    <Stack direction="row">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setStepDialog(index)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Remover"><IconButton size="small" onClick={() => setPrepSteps(current => current.filter((_, i) => i !== index))}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  }
                >
                  <ListItemButton onClick={() => setStepDialog(index)} sx={{ pr: 11 }}>
                    <ListItemText primary={prepStepText(step)} secondary={step.note || undefined} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setStepDialog(-1)} sx={{ mt: 1 }}>Passo antecipado</Button>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Notas e link</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {notesSummary}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField label="Notas" placeholder="Opcional" multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)} />
            <TextField
              label="Link"
              placeholder="Opcional (vídeo, site…)"
              type="url"
              value={link}
              onChange={e => setLink(e.target.value)}
              helperText={/^https?:\/\//i.test(link.trim()) ? <a href={link.trim()} target="_blank" rel="noopener noreferrer">Abrir o link</a> : undefined}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Outros nomes (voz)</Typography>
            <Typography variant="caption" color="text.secondary">{aliasesSummary}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Também a reconheço por estes nomes quando falas ou escreves («a massa da avó»).
          </Typography>
          <AliasEditor value={aliases} onChange={setAliases} />
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}

      {recipeId && (
        <Stack spacing={1.5} sx={{ mt: 3 }}>
          <Button variant="outlined" fullWidth startIcon={<ContentCopyIcon />} onClick={duplicate} disabled={busy}>Duplicar receita</Button>
          <Button color="error" variant="outlined" fullWidth onClick={remove} disabled={busy}>Apagar receita</Button>
        </Stack>
      )}

      {/* Always in reach */}
      <Paper
        elevation={3}
        sx={{ position: 'sticky', bottom: { xs: 64, md: 8 }, mt: 3, p: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', zIndex: 2 }}
      >
        <Button onClick={leave} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Guardar
        </Button>
      </Paper>

      <IngredientDialog
        open={ingredientDialog !== null}
        line={ingredientDialog?.line ?? null}
        items={items}
        onClose={() => setIngredientDialog(null)}
        onSave={saveIngredient}
        onRemove={removeIngredient}
      />
      <PrepStepDialog
        open={stepDialog !== null}
        step={stepDialog !== null && stepDialog >= 0 ? prepSteps[stepDialog] : null}
        onClose={() => setStepDialog(null)}
        onSave={step => {
          const index = stepDialog;
          setPrepSteps(current => (index !== null && index >= 0 ? current.map((s, i) => (i === index ? step : s)) : current.concat(step)));
          setStepDialog(null);
        }}
      />
      <SidesDialog
        open={sidesOpen}
        current={usualSides}
        options={sideRecipes}
        onClose={() => setSidesOpen(false)}
        onDone={sides => { setUsualSides(sides); setSidesOpen(false); }}
      />
      {confirmDialog}
    </Box>
  );
}

function SwitchRow({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <FormControlLabel
      labelPlacement="start"
      sx={{ mx: 0, justifyContent: 'space-between' }}
      control={<Switch checked={checked} onChange={e => onChange(e.target.checked)} />}
      label={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary">{hint}</Typography>
        </Box>
      }
    />
  );
}

/** "Passo antecipado": what to do and how many hours before the meal (24 = 1 day). */
function PrepStepDialog({ open, step, onClose, onSave }: {
  open: boolean; step: T.RecipePrepStep | null; onClose: () => void; onSave: (step: T.RecipePrepStep) => void;
}) {
  const [title, setTitle] = useState('');
  const [hoursText, setHoursText] = useState('24');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(step?.title ?? '');
    setHoursText(step ? number(step.leadTimeMinutes / 60, 2) : '24');
    setNote(step?.note ?? '');
    setError(null);
  }, [open, step]);

  const save = () => {
    if (!title.trim()) { setError('Indica o que é preciso fazer.'); return; }
    const hours = parseNumber(hoursText);
    if (hours === null || hours <= 0 || hours > 24 * 7) { setError('Indica um número de horas entre 1 e 168.'); return; }
    onSave({ title: title.trim(), leadTimeMinutes: Math.round(hours * 60), note: note.trim() || null });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Passo antecipado</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="O que é preciso fazer?" placeholder="Ex.: Demolhar bacalhau" value={title} onChange={e => setTitle(e.target.value)} autoFocus slotProps={{ htmlInput: { maxLength: 80 } }} />
          <TextField
            label="Horas antes da refeição"
            helperText="24 = 1 dia"
            value={hoursText}
            onChange={e => setHoursText(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField label="Nota" placeholder="Opcional" value={note} onChange={e => setNote(e.target.value)} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Ticks the usual side dishes among the side-dish recipes. */
function SidesDialog({ open, current, options, onClose, onDone }: {
  open: boolean; current: T.RecipeSide[]; options: T.RecipeSide[]; onClose: () => void; onDone: (sides: T.RecipeSide[]) => void;
}) {
  const [chosen, setChosen] = useState<T.RecipeSide[]>([]);
  useEffect(() => { if (open) setChosen(current); }, [open, current]);

  const all = options.concat(current.filter(c => !options.some(o => o.publicId === c.publicId)));
  const isChosen = (side: T.RecipeSide) => chosen.some(c => c.publicId === side.publicId);
  const toggle = (side: T.RecipeSide) =>
    setChosen(list => (isChosen(side) ? list.filter(c => c.publicId !== side.publicId) : list.concat(side)));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Acompanhamentos</DialogTitle>
      {all.length === 0 ? (
        <DialogContent>
          <DialogContentText>
            Ainda não há acompanhamentos. Cria receitas do tipo "Acompanhamento" (arroz, batata cozida, salada…).
          </DialogContentText>
        </DialogContent>
      ) : (
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense>
            {all.map(side => (
              <ListItemButton key={side.publicId} onClick={() => toggle(side)}>
                <ListItemIcon sx={{ minWidth: 36 }}><Checkbox edge="start" checked={isChosen(side)} tabIndex={-1} disableRipple /></ListItemIcon>
                <ListItemText primary={side.name} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      )}
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {all.length > 0 && <Button onClick={() => onDone([])} sx={{ mr: 'auto' }}>Sem acompanhamento</Button>}
        <Button onClick={onClose}>Cancelar</Button>
        {all.length > 0 && <Button variant="contained" onClick={() => onDone(chosen)}>Feito</Button>}
      </DialogActions>
    </Dialog>
  );
}

const RecipeEditPage: React.FC = () => <NeedsHousehold><RecipeEditContent /></NeedsHousehold>;

export default RecipeEditPage;
